<template>
  <v-menu
    width="18rem"
    rounded
    v-if="authStore.userData"
    :close-on-content-click="false"
    v-model="profileMenuOpen"
  >
    <template #activator="{ props }">
      <v-list-item v-bind="props" nav :active="false">
        <template #prepend>
          <v-avatar
            variant="outlined"
            :color="authStore.impersonationActive ? 'warning' : undefined"
            class="cursor-pointer"
            size="24"
          >
            <span>{{ userInitials[0] }}</span>
          </v-avatar>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.settings') }}
        </v-list-item-title>
        <template #append>
          <v-icon size="small" icon="mdi-chevron-right" />
        </template>
      </v-list-item>
    </template>
    <v-card>
      <v-card-text class="pb-0">
        <div class="mx-auto text-center">
          <v-avatar variant="outlined" class="cursor-pointer">
            <span>{{ userInitials }}</span>
          </v-avatar>
          <h3 class="text-capitalize mt-2">
            {{ authStore.userData.display_name }}
          </h3>
          <p class="text-caption">
            {{ authStore.userData.email }}
          </p>
          <p class="mt-4">
            {{ tenantDisplayName }}
          </p>
          <v-divider class="my-3"></v-divider>
        </div>
      </v-card-text>
      <v-list density="compact">
        <!-- Appearance with inline control -->
        <v-list-item>
          <template #prepend>
            <v-icon
              size="small"
              :icon="theme.name === 'dark' ? 'mdi-weather-night' : 'mdi-white-balance-sunny'"
            ></v-icon>
          </template>
          <template #title>
            <span>{{ $t('userMenuOptions.appearance') }}</span>
          </template>
          <template #append>
            <v-menu width="10rem">
              <template #activator="{ props }">
                <v-chip
                  v-bind="props"
                  label
                  append-icon="mdi-chevron-down"
                  size="small"
                  variant="outlined"
                >
                  {{ $t(`userMenuOptions.${chipDisplayTitle}`) }}
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  class="list-item-option"
                  v-for="themeOption in themeOptions"
                  :key="themeOption.value"
                  :title="$t(`userMenuOptions.${themeOption.value}`)"
                  @click="changeTheme(themeOption.value)"
                >
                  <template #prepend>
                    <v-icon size="small">{{ themeOption.icon }}</v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-list-item>

        <!-- Language with inline control -->
        <v-list-item>
          <template #prepend>
            <v-icon size="small" icon="mdi-translate"></v-icon>
          </template>
          <template #title>
            <span>{{ $t('userMenuOptions.language') }}</span>
          </template>
          <template #append>
            <v-menu width="10rem">
              <template #activator="{ props }">
                <v-chip
                  v-bind="props"
                  label
                  append-icon="mdi-chevron-down"
                  size="small"
                  variant="outlined"
                >
                  {{ $t(`languages.${$i18n.locale}`) }}
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  class="list-item-option"
                  v-for="locale in languageOptions"
                  :key="locale"
                  :title="$t(`languages.${locale}`)"
                  @click="selectLocale(locale)"
                >
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Personal Settings -->
        <v-list-item
          class="list-item-option"
          :title="$t('userMenuOptions.personalSettings')"
          :to="{ path: '/settings', query: { category: 'userPreferences' } }"
          :active="selectedCategory === 'userPreferences'"
          @click="handleCategoryClick('userPreferences')"
        >
          <template #prepend>
            <v-icon size="small"> mdi-account-cog-outline </v-icon>
          </template>
        </v-list-item>

        <!-- Organization Settings (Admin Only) -->
        <v-list-item
          v-if="authStore.userData.is_admin || authStore.userData.is_manager"
          class="list-item-option"
          :title="$t('userMenuOptions.organizationSettings')"
          :to="{ path: '/settings', query: { category: 'tenantConfiguration' } }"
          :active="selectedCategory === 'tenantConfiguration'"
          @click="handleCategoryClick('tenantConfiguration')"
        >
          <template #prepend>
            <v-icon size="small"> mdi-domain </v-icon>
          </template>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <v-list-item :title="$t('userMenuOptions.logout')" @click="logout">
          <template #prepend><v-icon size="small" icon="mdi-logout-variant"></v-icon> </template>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>
<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/store/auth'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { log } from '@/plugins/logger'
import { THEME_STORAGE_KEY } from '@/config/constants'
import { formatTenantName } from '@/utils/formatTenantName'

export default {
  name: 'UserMenu',
  setup() {
    const { locale } = useI18n()
    const theme = useTheme()

    return { locale, theme }
  },
  computed: {
    ...mapStores(useAuthStore),

    tenantDisplayName() {
      return formatTenantName(this.authStore.userData.company)
    },
    userInitials() {
      return this.authStore.userData.email
        .split('@')[0]
        .split('.')
        .map(n => n[0])
        .join('')
        .toUpperCase()
    },
    isDarkTheme() {
      return this.theme.name.value === 'dark'
    },
    chipDisplayTitle() {
      if (this.theme.isSystem.value) return 'system'

      const themeOption = this.themeOptions.find(t => t.value === this.theme.name.value)
      return themeOption.value
    }
  },
  data() {
    return {
      selectedLocale: 'en',
      languageOptions: ['en', 'ko', 'fr', 'cn'],
      profileMenuOpen: false,
      selectedCategory: null,
      themeOptions: [
        { value: 'light', icon: 'mdi-white-balance-sunny' },
        { value: 'dark', icon: 'mdi-weather-night' },
        { value: 'system', icon: 'mdi-laptop' }
      ]
    }
  },
  mounted() {
    // Initialize selected category based on current route
    if (this.$route.path === '/settings') {
      this.selectedCategory = this.$route.query.category || 'userPreferences'
    }
  },
  watch: {
    '$route.query.category': {
      handler(newCategory) {
        if (this.$route.path === '/settings') {
          this.selectedCategory = newCategory || 'userPreferences'
        }
      },
      immediate: true
    },
    '$route.path': {
      handler(newPath) {
        // Clear selection if not on settings page
        if (newPath !== '/settings') {
          this.selectedCategory = null
        }
      }
    }
  },
  props: {
    hoveringNavCard: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async logout() {
      await this.authStore.logoutUser()
      this.$router.push('/login')
    },
    changeTheme(themeName) {
      this.theme.change(themeName)
      localStorage.setItem(THEME_STORAGE_KEY, themeName)
      log.debug(`Theme changed to: ${themeName}`)
    },
    selectLocale(locale) {
      this.locale = locale
    },
    handleCategoryClick(category) {
      this.selectedCategory = category
      this.profileMenuOpen = false
    }
  }
}
</script>
<style lang="scss" scoped>
.appearing-text {
  display: none;
  opacity: 0;
  padding-left: 1rem;

  &.show {
    display: block;
    animation: fadeIn 0.2s ease-in-out 0.1s forwards;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.list-item-option {
  border-radius: 0.25rem !important;
}
</style>
